import axios from "axios";
import { captureException } from "@sentry/react";
import { debounce } from "lodash-es";
import { UPLOAD_API_BASE_URL } from "../../constants";
import { JsonStorage } from "../../utils/storage";
import { useEffect, useMemo, useState } from "react";

const wait = (ms) =>
	new Promise((res) => {
		setTimeout(res, ms);
	});
const debouncedWarning = debounce((func) => {
	func();
}, 20000);

const CHUNK_SIZE = 15 * 1024 * 1024;
const MAX_CONNECTIONS = 3;
const RETRY_WAITS = [2, 8, 32, 64];

const PROGRESS_KEY_PREFIX = "progress-";
const FAILED_COMPLETED_PART_KEY = "failed-completed-part-";
const HEARTBEAT_KEY_PREFIX = "heartbeat";
const HEARTBEAT_INTERVAL = 1;
const HEARTBEAT_THRESHOLD = 3;

const DEBUG = true;
const PART_NUMBER_TEMPLATE = "{partNumber}";
const SIGNATURE_TEMPLATE = "{signature}";

/**
 * Logger utility with different log levels
 */
const logger = {
	debug: (...args) => {
		if (DEBUG) console.debug("[Uploader]", ...args);
	},
	info: (...args) => {
		if (DEBUG) console.info("[Uploader]", ...args);
	},
	warn: (...args) => {
		console.warn("[Uploader]", ...args);
	},
	error: (...args) => {
		console.error("[Uploader]", ...args);
	},
};

// Unique id for this browser tab/session, so heartbeats can be attributed to
// the tab that wrote them. Without this, a tab can mistake its own (slightly
// stale, polled) heartbeat for an upload happening in another tab.
export const TAB_ID =
	(typeof crypto !== "undefined" && crypto.randomUUID
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export function getFingerPrint(file) {
	return ["v8", file.type, file.size, CHUNK_SIZE].join("-");
}

function getChunkSize({ file, video, index }) {
	const fileSize = file?.size || video?.size;
	const effectiveBaseChunkSize = video?.s3?.chunkSize || CHUNK_SIZE;
	const chunksCount =
		Object.keys(video?.s3?.parts)?.length ||
		getChunksCount(fileSize, effectiveBaseChunkSize);
	if (+index === chunksCount - 1) {
		return +index === 0
			? fileSize
			: fileSize - +index * effectiveBaseChunkSize;
	}
	return effectiveBaseChunkSize;
}

function getChunksCount(fileSize, baseChunkSize = CHUNK_SIZE) {
	if (fileSize === undefined || fileSize === null || fileSize === 0) {
		return 1;
	}

	return Math.max(Math.ceil(fileSize / baseChunkSize), 1);
}

function getPartUploadUrl({ video, part, index }) {
	if (part?.url) {
		return part.url;
	}
	if (!video?.s3?.partUrlTemplate || !part?.signature) {
		return null;
	}

	return video.s3.partUrlTemplate
		.replace(PART_NUMBER_TEMPLATE, String(Number(index) + 1))
		.replace(SIGNATURE_TEMPLATE, part.signature);
}

class Uploader {
	static getProgressKey(videoId) {
		return `uploader@${PROGRESS_KEY_PREFIX}${videoId}`;
	}

	static get heartbeatKey() {
		return `uploader@${HEARTBEAT_KEY_PREFIX}`;
	}

	constructor({
		file,
		video,
		onVideoChange,
		onError,
		onClearFile,
		onUploadStateChange,
		onProgressChange,
		httpClient,
		message,
		modal,
	}) {
		this.file = file;
		this.video = video;
		this.setVideo = (v) => {
			onVideoChange(v);
			this.video = v;
		};
		this.failedCompletedParts = [];
		this.onError =
			onError ||
			((error) => {
				logger.error("Upload error:", error);
				captureException(error);
			});
		this.clearFile = onClearFile || (() => {});
		this.httpClient =
			httpClient || axios.create({ baseURL: UPLOAD_API_BASE_URL });
		this.message = message;
		this.modal = modal;

		this.progressKey = Uploader.getProgressKey(this.video?.id);
		this.failedCompletedPartKey = `uploader@${FAILED_COMPLETED_PART_KEY}${this.video?.id}`;
		this.heartbeatKey = Uploader.heartbeatKey;
		this.onUploadStateChange = onUploadStateChange || (() => {});
		this.onProgressChange = onProgressChange || (() => {});
		this.progress = 0;

		this.checkExistingHeartbeat();

		this.isPaused = false;

		this.currentUploadParts = new Map();

		logger.info("Uploader initialized", {
			videoId: this.video?.id,
			fileName: this.file?.name,
			fileSize: this.file?.size,
			fileType: this.file?.type,
			initialProgress: this.progress,
		});
	}

	get parts() {
		return this.video?.s3?.parts || {};
	}

	cleanupStorageListener() {
		if (this.storageListener) {
			window.removeEventListener("storage", this.storageListener);
			this.storageListener = null;
		}
	}

	abortAllUploads() {
		logger.info("Aborting all uploads");
		Object.values(this.parts).forEach((p) => {
			if (p.controller && typeof p.controller.abort === "function") {
				p.controller.abort();
			}
		});
	}

	completePart({ index, ETag }) {
		logger.info("Completing part", { index, ETag });
		return this.httpClient
			.put(`/videos/${this.video.id}/upload/${index}/complete`, { ETag })
			.then(({ data: { data } }) => {
				logger.debug("Part completed successfully", { index, ETag });
				this.setVideo(data);
				return data;
			})
			.catch((error) => {
				logger.error("Error completing part", { index, error });
				captureException(error);
				if (JsonStorage.get(this.failedCompletedPartKey)) {
					return Promise.reject(error);
				}
				JsonStorage.set({
					key: this.failedCompletedPartKey,
					value: { index, ETag },
				});
			});
	}

	checkCompletedParts() {
		logger.debug("Checking completed parts");
		Object.entries(this.parts).forEach(([index, { ETag }]) => {
			if (!ETag || !this.video?.s3?.parts?.[index]) {
				return;
			}
			if (this.video.s3.parts[index].ETag !== ETag) {
				logger.debug("Found part to complete", { index, ETag });
				this.completePart({ ETag, index });
			}
		});
		const failedCompletedPart = JsonStorage.get(
			this.failedCompletedPartKey
		);
		if (failedCompletedPart) {
			this.completePart({
				ETag: failedCompletedPart.ETag,
				index: failedCompletedPart.index,
			}).then(() => {
				JsonStorage.remove(this.failedCompletedPartKey);
			});
		}
	}

	updateProgress() {
		if (!this.video?.size) {
			this.progress = 0;
			return this.progress;
		}

		const totalLoaded = Object.entries(this.parts).reduce(
			(acc, [index, part]) => {
				if (!part) {
					return acc;
				}
				if (part.ETag) {
					return (
						acc +
						getChunkSize({
							file: this.file,
							video: this.video,
							index,
						})
					);
				}
				const uploadingPart = this.currentUploadParts.get(index);
				if (uploadingPart) {
					return acc + uploadingPart.uploaded;
				}
				return acc;
			},
			0
		);

		let percent;
		if (totalLoaded === this.video.size) {
			percent = Object.values(this.parts).every((p) => p.ETag) ? 100 : 99;
		} else {
			percent = this.video?.s3?.parts?.[0]
				? (totalLoaded / this.video.size) * 100
				: 0;
		}

		if (this.progress !== percent) {
			this.onProgressChange(percent);
		}
		this.progress = percent;

		logger.debug("Progress calculated", {
			percent: this.progress.toFixed(2),
			loaded: `${(totalLoaded / (1024 * 1024)).toFixed(2)} MB`,
			total: `${(this.video.size / (1024 * 1024)).toFixed(2)} MB`,
		});
	}

	uploadChunk({ part, chunk, index }) {
		const uploadUrl = getPartUploadUrl({ video: this.video, part, index });
		if (!uploadUrl) {
			this.onError(new Error("خطا هنگام آماده‌سازی آپلود!"));
			return Promise.resolve();
		}
		logger.info("Uploading chunk", {
			index,
			chunkSize: `${(chunk.size / (1024 * 1024)).toFixed(2)} MB`,
		});

		const controller = new AbortController();
		this.currentUploadParts.set(index, {
			controller,
			retry: (this.currentUploadParts.get(index)?.retry || -1) + 1,
			active: true,
			uploaded: 0,
		});

		let abortTimeout;
		const options = {
			signal: controller.signal,
			onUploadProgress: (event) => {
				const { loaded } = event;
				clearTimeout(abortTimeout);
				abortTimeout = setTimeout(() => {
					logger.warn("Upload stalled, aborting chunk", { index });
					controller?.abort();
				}, 15000);
				this.currentUploadParts.set(index, {
					...this.currentUploadParts.get(index),
					retry: 0,
					uploaded: loaded,
				});
				this.updateProgress();
			},
			headers: {
				"Content-Type": this.file.type,
				Authorization: undefined,
			},
		};

		return this.httpClient
			.put(uploadUrl, chunk, options)
			.then((res) => {
				clearTimeout(abortTimeout);
				const ETag = res.headers.etag.replaceAll('"', "");
				logger.info("Chunk uploaded successfully", { index, ETag });
				this.currentUploadParts.delete(index);
				this.completePart({ index, ETag }).finally(() => {
					this.updateProgress();
					this.processNextChunk();
				});
			})
			.catch((error) => {
				clearTimeout(abortTimeout);
				const retry = this.currentUploadParts.get(index)?.retry || 0;
				if (axios.isCancel(error)) {
					logger.info("Chunk upload canceled", { index });
				} else {
					logger.error("Error uploading chunk", { index, error });
					captureException(error);
				}

				if (retry === 2 && this.message) {
					debouncedWarning(() => {
						logger.warn("Slow connection detected");
						this.message.warning(
							"در صورت امکان از اینترنت پر سرعت تری استفاده کنید!",
							5
						);
					});
				}

				if (retry > RETRY_WAITS.length) {
					logger.error(
						`Part#${index} failed to upload, giving up after ${retry} retries`
					);
					this.onError(new Error("خطا هنگام آپلود!"));
					return;
				}

				const backOffTime = RETRY_WAITS[retry];
				logger.warn(
					`Part#${index}/${retry} failed to upload, backing off ${backOffTime}s before retrying...`
				);

				wait(backOffTime * 1000).then(() => {
					this.currentUploadParts.set(index, {
						retry: retry + 1,
						active: false,
					});
					this.processNextChunk();
				});
			});
	}

	processNextChunk() {
		if (this.isPaused) {
			logger.debug("Upload is paused, not processing next chunk");
			return;
		}

		if (
			this.video.fingerprint &&
			this.video.fingerprint !== getFingerPrint(this.file)
		) {
			logger.warn("Fingerprint mismatch, not processing next chunk");
			return;
		}

		const activeConnections = Array.from(
			this.currentUploadParts.values()
		).filter((upload) => upload.active);
		logger.debug("Processing next chunk", {
			activeConnections,
			maxConnections: MAX_CONNECTIONS,
		});

		if (activeConnections.length >= MAX_CONNECTIONS) {
			logger.debug("Max connections reached, not starting new chunk");
			return;
		}

		const [index, part] =
			Object.entries(this.parts).find(
				([index, p]) =>
					!p.ETag && !this.currentUploadParts.get(index)?.active
			) || [];

		if (!part) {
			logger.debug("No more chunks to upload");
			return;
		}

		const effectiveBaseChunkSize = this.video?.s3?.chunkSize || CHUNK_SIZE;
		const start = +index * effectiveBaseChunkSize;
		const actualSliceSize = getChunkSize({
			file: this.file,
			video: this.video,
			index: +index,
		});
		logger.debug("Found chunk to upload", {
			index,
			start,
			chunkSize: actualSliceSize,
		});

		const chunk = this.file.slice(start, start + actualSliceSize);
		this.uploadChunk({ chunk, part, index });
	}

	updateFileState() {
		if (!this.video?.id) {
			return Promise.resolve();
		}

		if (
			this.video.fileState === "UPLOADING" ||
			this.percent === 0 ||
			this.percent === 100
		) {
			return Promise.resolve();
		}

		logger.info("Updating file state to UPLOADING");
		return this.httpClient
			.put(`/videos/${this.video.id}/file-state`, {
				fileState: "UPLOADING",
			})
			.then(({ data: { data } }) => {
				logger.debug("File state updated successfully");
				this.setVideo(data);
				return data;
			})
			.catch((error) => {
				logger.error("Error updating file state", { error });
				captureException(error);
				return Promise.reject(error);
			});
	}

	initializeUpload() {
		if (!this.file || !this.video?.id) {
			logger.warn("Missing file or video ID, cannot initialize upload");
			return Promise.resolve();
		}

		const currentFingerprint = getFingerPrint(this.file);
		logger.info("Initializing upload", {
			videoId: this.video.id,
			fingerprint: this.video.fingerprint,
			fileFingerprint: currentFingerprint,
		});

		this.updateProgress();

		if (
			this.parts?.[0] &&
			this.video.fingerprint &&
			this.video.fingerprint === currentFingerprint
		) {
			logger.info("Resuming upload with existing parts");

			return Promise.resolve();
		}

		if (
			this.video.fingerprint &&
			this.video.fingerprint !== currentFingerprint
		) {
			logger.warn("Fingerprint mismatch, prompt for restart");
			if (this.modal) {
				return new Promise((resolve, reject) => {
					this.modal.confirm({
						title: "ادامه آپلود",
						content:
							"امکان ادامه آپلود وجود ندارد، از ابتدا آپلود شود؟",
						onOk: () => {
							this._initializeUpload()
								.then(resolve)
								.catch(reject);
						},
						onCancel: () => {
							this.clearFile();
							logger.info(
								"Upload canceled by user due to fingerprint mismatch"
							);
						},
						okText: "بله",
						cancelText: "خیر",
					});
				});
			}
			return this._initializeUpload();
		}

		return this._initializeUpload();
	}

	_initializeUpload() {
		const partsCount = getChunksCount(
			this.file.size,
			this.video?.s3?.chunkSize || CHUNK_SIZE
		);
		logger.info("Initializing new upload", {
			filename: this.file.name,
			size: this.file.size,
			partsCount,
		});

		return this.httpClient
			.post(`/videos/${this.video.id}/upload/init`, {
				filename: this.file.name,
				fingerprint: getFingerPrint(this.file),
				partsCount,
				size: this.file.size,
			})
			.then((res) => {
				logger.info("Upload initialized successfully", {
					videoId: this.video.id,
					partsCount: Object.keys(res.data.data.s3.parts).length,
				});

				this.setVideo(res.data.data);
			})
			.catch((err) => {
				logger.error("Error initializing upload", { error: err });
				this.onError(err);
				throw err;
			});
	}

	cleanupStorage() {
		try {
			localStorage.removeItem(this.progressKey);
			localStorage.removeItem(this.heartbeatKey);
		} catch (error) {
			logger.error("Error cleaning up storage:", error);
		}
	}

	updateHeartbeat() {
		logger.info("Updating heartbeat", this.heartbeatKey);
		try {
			const timestamp = Date.now();
			JsonStorage.set({
				key: this.heartbeatKey,
				value: {
					timestamp,
					videoId: this.video.id,
					progress: this.progress,
					tabId: TAB_ID,
				},
			});
			logger.debug("Heartbeat updated", {
				key: this.heartbeatKey,
				timestamp,
			});
		} catch (error) {
			logger.error("Error updating heartbeat:", error);
		}
	}

	startHeartbeat() {
		this.stopHeartbeat();
		logger.info("Starting heartbeat");
		this.updateHeartbeat();
		this._heartbeatInterval = setInterval(() => {
			this.updateHeartbeat();
		}, HEARTBEAT_INTERVAL * 1000);
		logger.debug("Heartbeat started", { interval: HEARTBEAT_INTERVAL });
	}

	stopHeartbeat() {
		if (this._heartbeatInterval) {
			clearInterval(this._heartbeatInterval);
			this._heartbeatInterval = null;
			logger.debug("Heartbeat stopped");

			try {
				localStorage.removeItem(this.heartbeatKey);
			} catch (error) {
				logger.error("Error removing heartbeat data:", error);
			}
		}
	}

	start() {
		logger.info("Starting upload");
		this.isPaused = false;
		this.isUploading = true;

		return Promise.resolve()
			.then(() => this.initializeUpload())
			.then(() => {
				this.updateFileState();
				this.startHeartbeat();
				this.onUploadStateChange("uploading");

				logger.info(
					`Starting ${MAX_CONNECTIONS} concurrent chunk uploads`
				);

				if (!this._processNextChunkInterval) {
					logger.debug("Setting up process next chunk interval");
					this._processNextChunkInterval = setInterval(() => {
						this.processNextChunk();
					}, 5000);
				}

				if (!this._checkCompletedPartsInterval) {
					logger.debug("Setting up completed parts check interval");
					this._checkCompletedPartsInterval = setInterval(() => {
						this.checkCompletedParts();
					}, 3000);
				}

				return this;
			});
	}

	pause() {
		logger.info("Pausing upload");
		this.isPaused = true;
		this.isUploading = false;
		this.onUploadStateChange("paused");

		this.currentUploadParts.forEach(({ controller }) => {
			if (controller && typeof controller.abort === "function") {
				controller.abort();
			}
		});
		this.currentUploadParts.clear();

		if (this._checkCompletedPartsInterval) {
			logger.debug("Clearing completed parts check interval");
			clearInterval(this._checkCompletedPartsInterval);
			this._checkCompletedPartsInterval = null;
		}

		if (this._processNextChunkInterval) {
			logger.debug("Clearing process next chunk interval");
			clearInterval(this._processNextChunkInterval);
			this._processNextChunkInterval = null;
		}

		return this;
	}

	resume() {
		logger.info("Resuming upload");

		if (!this.file) {
			logger.warn("Cannot resume upload: No file available");
			return Promise.reject(new Error("No file available for upload"));
		}

		return this.start();
	}

	destroy() {
		logger.info("Destroying uploader instance");
		this.pause();
		this.stopHeartbeat();
		this.abortAllUploads();
		this.cleanupStorageListener();
	}

	checkExistingHeartbeat() {
		const existingHeartbeat = JsonStorage.get(this.heartbeatKey);
		if (existingHeartbeat && existingHeartbeat.timestamp) {
			const now = Date.now();
			const timeSinceLastHeartbeat =
				(now - existingHeartbeat.timestamp) / 1000;

			if (timeSinceLastHeartbeat <= HEARTBEAT_THRESHOLD) {
				logger.warn("Recent heartbeat detected", {
					key: this.heartbeatKey,
					timeSinceLastHeartbeat: `${timeSinceLastHeartbeat.toFixed(
						1
					)}s`,
					threshold: `${HEARTBEAT_THRESHOLD}s`,
				});

				throw new Error("یک آپلود در حال انجام است");
			} else {
				logger.info("Stale heartbeat found, will be overwritten", {
					key: this.heartbeatKey,
					timeSinceLastHeartbeat: `${timeSinceLastHeartbeat.toFixed(
						1
					)}s`,
				});
			}
		}
	}
}

Uploader.useUploadingVideo = () => {
	const [uploadingVideo, setUploadingVideo] = useState(null);

	useEffect(() => {
		function checkHeartbeat() {
			const heartbeat = JsonStorage.get(Uploader.heartbeatKey);
			if (
				heartbeat &&
				heartbeat.timestamp &&
				Date.now() - heartbeat.timestamp < 1000 * 2
			) {
				setUploadingVideo({
					id: heartbeat.videoId,
					progress: heartbeat.progress,
					tabId: heartbeat.tabId,
				});
			} else {
				setUploadingVideo(null);
			}
		}
		checkHeartbeat();
		const interval = setInterval(checkHeartbeat, 1500);
		return () => clearInterval(interval);
	}, []);

	return uploadingVideo;
};

export default Uploader;
